/**
 * A/B de UX do cadastro — signup-ux-ab-2026-09 (front).
 *
 * Dois braços, sorteio determinístico por patientId:
 *
 *   control  fluxo atual: botão "Continuar" em toda etapa, feedback só de
 *            erro, tela dedicada de Termos de Uso no fim.
 *   guided   pacote completo:
 *              - CTA da Home "Checar médicos disponíveis";
 *              - cadastro sem fade, fundo fixo, barra no topo e chips
 *                acumulados (nome ✓, e-mail ✓, …);
 *              - CTA em primeira pessoa ("Este é o meu e-mail") no lugar do
 *                "Continuar" genérico, e estado de carregamento que nomeia o
 *                que está acontecendo;
 *              - check verde no campo assim que o valor fica válido (hoje o
 *                paciente só recebe sinal quando erra);
 *              - o nome digitado volta como saudação nas telas seguintes;
 *              - aceite dos Termos embutido na última tela de dado, com link
 *                para o texto completo, em vez de uma tela só para isso.
 *
 * Por que um braço só com o pacote inteiro: o cadastro tem seis telas curtas
 * e o volume atual não sustenta isolar cada peça sem esticar o teste por
 * meses. O desenho mede o pacote; atribuir o efeito a uma peça específica
 * exigiria uma segunda rodada.
 *
 * NADA de promessa de tempo no copy dos braços ("em minutos", "agora",
 * "imediato"): o atendimento tem estado de descanso e os Termos declaram os
 * tempos como médias informativas, não prazo. Ver
 * `.cursor/rules/google-ads-diretrizes-marca.mdc` §2.1.
 *
 * DESLIGADO (21/09/2026): nulo em cadastro→pago (54,4% vs 54,3%, p=0,98)
 * e em primeira compra (29,3% vs 28,2%, p=0,54). `SIGNUP_UX_AB_LIVE = false`
 * devolve todo mundo ao controle sem deploy de backend. Preview
 * `?signup_ux=guided|control` continua forçando o braço.
 */

import { getSignupSteps, signupProgress, stepForRoute } from './signupOrderAb';

/** Rotas do cadastro em que o braço `guided` tira o fade e mostra o chrome. */
export const GUIDED_SIGNUP_PATHS = Object.freeze([
  '/consulta',
  '/email',
  '/email/confirm',
  '/cpf',
  '/telefone',
  '/cadastro/documento',
  '/cupom-primeira',
  '/termos',
]);

export function isGuidedSignupPath(pathname) {
  const p = String(pathname || '').replace(/\/+$/, '') || '/';
  return GUIDED_SIGNUP_PATHS.includes(p);
}

const KEY = 'signupUxVariant';
const KEY_FOR = 'signupUxVariantFor';

export const SIGNUP_UX_CONTROL = 'control';
export const SIGNUP_UX_GUIDED = 'guided';

export const SIGNUP_UX_VARIANTS = [SIGNUP_UX_CONTROL, SIGNUP_UX_GUIDED];

/**
 * Kill switch de produto. True = sorteio 50/50. False = ninguém é
 * sorteado e `getSignupUxVariant` devolve o controle.
 */
export const SIGNUP_UX_AB_LIVE = false;

function currentPatientId() {
  try {
    return (
      sessionStorage.getItem('patientId') ||
      localStorage.getItem('patientId') ||
      ''
    );
  } catch {
    return '';
  }
}

function fnv1a32(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h >>> 0;
}

/** Sorteio bruto 50/50 (ignora o kill switch). Só para teste e auditoria. */
export function hashSignupUxVariant(patientId) {
  const pid = String(patientId || '');
  if (!pid) return SIGNUP_UX_CONTROL;
  const roll = fnv1a32(`signup-ux-ab-2026-09:${pid}`);
  return SIGNUP_UX_VARIANTS[roll % SIGNUP_UX_VARIANTS.length];
}

export function assignSignupUxVariant(patientId) {
  if (!SIGNUP_UX_AB_LIVE) return SIGNUP_UX_CONTROL;
  const variant = hashSignupUxVariant(patientId);
  setSignupUxVariant(variant, patientId);
  return variant;
}

export function setSignupUxVariant(variant, patientId) {
  const v = String(variant || '');
  if (!SIGNUP_UX_VARIANTS.includes(v)) return;
  const pid = String(patientId || currentPatientId() || '');
  if (!pid) return;
  try {
    localStorage.setItem(KEY, v);
    localStorage.setItem(KEY_FOR, pid);
  } catch {}
}

/**
 * Braço atribuído, ou null quando o paciente está fora do experimento
 * (kill switch off, sem patientId, braço gravado para outro paciente). A
 * telemetria usa esta versão: "fora do experimento" e "controle" são
 * populações diferentes e não podem virar a mesma coisa na análise.
 */
export function getSignupUxVariantRaw() {
  try {
    const pid = currentPatientId();
    if (!pid) return null;
    if (localStorage.getItem(KEY_FOR) !== pid) return null;
    const v = localStorage.getItem(KEY);
    return SIGNUP_UX_VARIANTS.includes(v) ? v : null;
  } catch {
    return null;
  }
}

function previewOverride() {
  try {
    if (typeof window === 'undefined') return null;
    const q = new URLSearchParams(window.location.search).get('signup_ux');
    if (q === SIGNUP_UX_GUIDED || q === SIGNUP_UX_CONTROL) {
      sessionStorage.setItem('signupUxPreview', q);
      return q;
    }
    const stored = sessionStorage.getItem('signupUxPreview');
    if (stored === SIGNUP_UX_GUIDED || stored === SIGNUP_UX_CONTROL) return stored;
  } catch {}
  return null;
}

/** Braço do paciente atual. Sempre utilizável: controle por padrão. */
export function getSignupUxVariant() {
  const preview = previewOverride();
  if (preview) return preview;
  if (!SIGNUP_UX_AB_LIVE) return SIGNUP_UX_CONTROL;
  return getSignupUxVariantRaw() || SIGNUP_UX_CONTROL;
}

export function clearSignupUxVariant() {
  try {
    localStorage.removeItem(KEY);
    localStorage.removeItem(KEY_FOR);
  } catch {}
}

/** CTA em primeira pessoa, check verde, saudação com o nome. */
export function usesGuidedCopy(variant = getSignupUxVariant()) {
  return variant === SIGNUP_UX_GUIDED;
}

/**
 * Aceite dos Termos embutido na última tela de dado (link para o texto
 * completo + botão que declara o aceite), sem a tela dedicada `/termos`.
 *
 * Só o braço `guided` faz isso. Quem consome precisa manter os dois desfechos
 * que hoje vivem em TermsOfUse.jsx: `blocked` (paciente barrado por
 * descumprimento anterior) e `loadingTrap`.
 */
export function usesInlineTerms(variant = getSignupUxVariant()) {
  return variant === SIGNUP_UX_GUIDED;
}

/**
 * Progresso visual. No braço `guided` o aceite mora na última tela de dado,
 * então `/termos` some da conta — senão a barra parava em "5 de 6" no
 * telefone e o paciente achava que ainda faltava uma etapa.
 */
function pathForUxProgress(pathname) {
  const p = String(pathname || '').replace(/\/+$/, '') || '/';
  // Parada opcional entre CPF e o próximo dado — conta como a etapa do CPF
  // para a barra não pular para o fim (5 de 5) no meio do funil.
  return p === '/cupom-primeira' ? '/cpf' : pathname;
}

export function signupUxProgress(pathname, orderVariant, uxVariant = getSignupUxVariant()) {
  const progressPath = pathForUxProgress(pathname);
  if (!usesInlineTerms(uxVariant)) return signupProgress(progressPath, orderVariant);
  const steps = getSignupSteps(orderVariant).filter((s) => s !== 'termos');
  const step = stepForRoute(progressPath);
  if (step === 'termos') {
    return { currentStep: steps.length, totalSteps: Math.max(1, steps.length) };
  }
  const idx = step ? steps.indexOf(step) : -1;
  return {
    currentStep: idx >= 0 ? idx + 1 : Math.max(1, steps.length),
    totalSteps: Math.max(1, steps.length),
  };
}

const CHIP_META = Object.freeze({
  nome: { label: 'Nome', keys: ['fullName', 'patientName'] },
  email: { label: 'E-mail', keys: ['patientEmail'] },
  cpf: { label: 'CPF', keys: ['patientCpf'] },
  telefone: { label: 'Telefone', keys: ['patientPhone'] },
});

function readStoredValue(keys) {
  for (const key of keys) {
    try {
      const raw = sessionStorage.getItem(key) || localStorage.getItem(key);
      if (raw && String(raw).trim()) return String(raw).trim();
    } catch {}
  }
  return '';
}

function formatChipValue(step, raw) {
  if (step === 'cpf') {
    const d = raw.replace(/\D/g, '');
    if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  return raw;
}

/**
 * Campos já concluídos, na ordem do funil, para os chips acima do card.
 * Só entra etapa anterior à atual e com valor gravado na sessão.
 */
export function completedSignupChips(pathname, orderVariant, uxVariant = getSignupUxVariant()) {
  const steps = usesInlineTerms(uxVariant)
    ? getSignupSteps(orderVariant).filter((s) => s !== 'termos')
    : getSignupSteps(orderVariant);
  const path = String(pathname || '').replace(/\/+$/, '') || '/';
  let current = stepForRoute(path);
  if (!current && path === '/cupom-primeira') current = 'telefone';
  const currentIdx = current ? steps.indexOf(current) : -1;
  if (currentIdx < 0) return [];

  const chips = [];
  for (let i = 0; i < currentIdx; i += 1) {
    const step = steps[i];
    const meta = CHIP_META[step];
    if (!meta) continue;
    const value = readStoredValue(meta.keys);
    if (!value) continue;
    chips.push({ id: step, label: meta.label, value: formatChipValue(step, value) });
  }
  return chips;
}
